
import React, { useState, useEffect } from 'react';
import { UserProfile, AnxietyEntry, AnxietyType, Language, CBTEntry, FearLadderItem } from '../types';
// Fix: Added missing types to imports and corrected state handling to include required IDs
import { analyzeAnxietyContext, generateFearLadder, generateCBTAnalysis } from '../services/geminiService';
import { TRANSLATIONS } from '../translations';
import { Wind, AlertTriangle, Activity, Phone, Shield, ArrowRight, Loader2, Sparkles, Brain, Eye, Plus, CheckCircle, X, List, HeartPulse, Save, ShieldAlert, Thermometer, UserCheck, MessageSquare } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const CalmHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'checkin' | 'ladder' | 'cbt' | 'safety'>('checkin');
  const [inputFeelings, setInputFeelings] = useState('');
  const [anxietyLevel, setAnxietyLevel] = useState(3);
  const [isEmergency, setIsEmergency] = useState(false);
  const [showSuicideWarning, setShowSuicideWarning] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<{level: number, type: AnxietyType, suggestion: string} | null>(null);
  
  // CBT State - Updated with correct type
  const [cbtInput, setCbtInput] = useState('');
  const [loadingCbt, setLoadingCbt] = useState(false);
  const [cbtResult, setCbtResult] = useState<CBTEntry | null>(null);

  // Ladder State - Updated with correct type
  const [fearInput, setFearInput] = useState('');
  const [loadingLadder, setLoadingLadder] = useState(false);
  const [ladderResult, setLadderResult] = useState<FearLadderItem | null>(null);

  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH];

  const [breathState, setBreathState] = useState<'inhale' | 'hold' | 'exhale' | 'pause'>('inhale');
  const [breathText, setBreathText] = useState(t.breath_inhale);
  
  useEffect(() => {
    let interval: any;
    if (isEmergency) {
      let phase = 0;
      interval = setInterval(() => {
        phase = (phase + 1) % 4;
        if (phase === 0) { setBreathState('inhale'); setBreathText(t.breath_inhale); }
        else if (phase === 1) { setBreathState('hold'); setBreathText(t.breath_hold); }
        else if (phase === 2) { setBreathState('exhale'); setBreathText(t.breath_exhale); }
        else if (phase === 3) { setBreathState('pause'); setBreathText(t.breath_pause); }
      }, 4000); 
    }
    return () => clearInterval(interval);
  }, [isEmergency, t]);

  const checkForDanger = (text: string): boolean => {
      const dangerWords = ['خودکشی', 'بمیرم', 'مرگ', 'کشتن خودم', 'تمامش کنم', 'suicide', 'kill myself', 'hurt myself', 'خۆکوشتن', 'بمرم', 'مردن'];
      return dangerWords.some(word => text.toLowerCase().includes(word.toLowerCase()));
  };

  const handleCheckIn = async () => {
    if (!inputFeelings.trim()) return;
    if (checkForDanger(inputFeelings)) { setShowSuicideWarning(true); return; }
    setLoadingAnalysis(true);
    try {
      const result = await analyzeAnxietyContext(user, inputFeelings);
      setAnalysisResult(result);
      setAnxietyLevel(result.level);
      if (result.level >= 8) setIsEmergency(true);
      const newEntry: AnxietyEntry = { id: Date.now().toString(), timestamp: Date.now(), level: result.level, trigger: inputFeelings, type: result.type, interventionUsed: result.level >= 8 ? 'Emergency Mode' : 'Check-in' };
      onUpdateUser({ ...user, anxietyProfile: { ...user.anxietyProfile, history: [...user.anxietyProfile.history, newEntry], triggers: Array.from(new Set([...user.anxietyProfile.triggers, result.type])), lastCheckIn: Date.now() } });
    } catch (e) { console.error(e); } finally { setLoadingAnalysis(false); }
  };

  const handleCBT = async () => {
    if (!cbtInput.trim()) return;
    setLoadingCbt(true);
    try {
      const res = await generateCBTAnalysis(user, cbtInput);
      // Fix: Adding ID to the entry
      const newEntry: CBTEntry = { ...res, id: Date.now().toString() };
      setCbtResult(newEntry);
      onUpdateUser({ ...user, anxietyProfile: { ...user.anxietyProfile, cbtHistory: [newEntry, ...user.anxietyProfile.cbtHistory] } });
    } catch (e) { console.error(e); } finally { setLoadingCbt(false); } // Fix: Corrected loading state
  };

  const handleLadder = async () => {
    if (!fearInput.trim()) return;
    setLoadingLadder(true);
    try {
      const res = await generateFearLadder(user, fearInput);
      // Fix: Adding ID to the entry
      const newEntry: FearLadderItem = { ...res, id: Date.now().toString() };
      setLadderResult(newEntry);
      onUpdateUser({ ...user, anxietyProfile: { ...user.anxietyProfile, fearLadder: [newEntry, ...user.anxietyProfile.fearLadder] } });
    } catch (e) { console.error(e); } finally { setLoadingLadder(false); }
  };

  if (showSuicideWarning) {
      return (
          <div className="fixed inset-0 z-[60] bg-rose-900/95 flex items-center justify-center p-6 text-white text-center animate-fade-in">
              <div className="max-w-md w-full bg-white text-slate-900 p-8 rounded-3xl shadow-2xl">
                  <AlertTriangle className="w-16 h-16 text-rose-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-4 text-rose-700">{t.calm_emergency_alert}</h2>
                  <p className="mb-6 text-slate-600 leading-relaxed">{t.calm_emergency_desc}</p>
                  <div className="space-y-3 mb-6">
                      <a href="tel:123" className="block bg-rose-100 hover:bg-rose-200 text-rose-800 py-3 rounded-xl font-bold">{t.calm_emergency_social}</a>
                      <a href="tel:1480" className="block bg-teal-100 hover:bg-teal-200 text-teal-800 py-3 rounded-xl font-bold">{t.calm_emergency_mental}</a>
                  </div>
                  <button onClick={() => setShowSuicideWarning(false)} className="text-slate-400 text-sm hover:text-slate-600 underline">{t.calm_back_to_app}</button>
              </div>
          </div>
      );
  }

  if (isEmergency) {
    return (
      <div className="fixed inset-0 z-50 bg-[#050810] text-white flex flex-col items-center justify-center p-6 animate-fade-in overflow-hidden">
        <button onClick={() => setIsEmergency(false)} className="absolute top-8 left-8 p-3 bg-white/5 rounded-full hover:bg-white/10 transition-colors"><X className="w-6 h-6" /></button>
        <div className="mb-12 text-center animate-pulse">
            <h2 className="text-4xl font-black mb-2 text-teal-400 tracking-tighter uppercase">{user.language === Language.KURDISH ? 'تەنها هەناسە بدە' : 'فقط نفس بکش'}</h2>
            <p className="text-slate-500 font-bold">{user.language === Language.KURDISH ? 'هەموو شتێک باش دەبێت' : 'همه چیز خوب خواهد شد'}</p>
        </div>
        
        <div className="relative flex items-center justify-center w-80 h-80 mb-20">
          <div className={`absolute rounded-full bg-teal-500/20 blur-3xl transition-all duration-[4000ms] ${breathState === 'inhale' ? 'scale-[2.5] opacity-60' : 'scale-100 opacity-20'}`}></div>
          <div className={`absolute rounded-full bg-teal-400 shadow-[0_0_100px_rgba(45,212,191,0.5)] transition-all duration-[4000ms] ease-in-out ${breathState === 'inhale' ? 'w-64 h-64' : 'w-24 h-24'}`}></div>
          <div className="absolute text-2xl font-black text-white text-center drop-shadow-lg">{breathText}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl relative z-10">
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-3xl border border-white/10">
                <h3 className="font-bold mb-4 text-teal-300 flex items-center gap-2"><Eye className="w-5 h-5" /> {t.grounding_title}</h3>
                <div className="text-sm space-y-3 text-slate-300">
                   <div className="flex items-center gap-3 bg-white/5 p-2 rounded-xl"><span className="text-lg">👀</span> {t.grounding_eye}</div>
                   <div className="flex items-center gap-3 bg-white/5 p-2 rounded-xl"><span className="text-lg">✋</span> {t.grounding_hand}</div>
                   <div className="flex items-center gap-3 bg-white/5 p-2 rounded-xl"><span className="text-lg">👂</span> {t.grounding_ear}</div>
                </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-3xl border border-white/10 flex flex-col justify-center gap-4">
                <button className="w-full py-4 bg-teal-500 text-white rounded-2xl font-black shadow-lg shadow-teal-500/20 flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" /> {t.calm_emergency_social}
                </button>
                <p className="text-[10px] text-center text-slate-500 uppercase font-black tracking-widest">{user.language === Language.KURDISH ? 'تۆ بەتەنیا نیت' : 'تو تنها نیستی'}</p>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20 bg-slate-50/30">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
           <h2 className="text-3xl font-black text-slate-800 flex items-center gap-3"><Wind className="w-8 h-8 text-teal-500" />{t.calm_companion}</h2>
           <p className="text-slate-500 text-sm mt-1 font-medium">{t.anxiety_management}</p>
        </div>
        <div className="flex bg-white rounded-2xl border border-slate-200 p-1.5 shadow-sm overflow-x-auto no-scrollbar">
            <button onClick={() => setActiveTab('checkin')} className={`px-4 py-2 rounded-xl text-sm font-black transition-all whitespace-nowrap ${activeTab === 'checkin' ? 'bg-teal-500 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>{t.checkin_tab}</button>
            <button onClick={() => setActiveTab('ladder')} className={`px-4 py-2 rounded-xl text-sm font-black transition-all whitespace-nowrap ${activeTab === 'ladder' ? 'bg-teal-500 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>{t.ladder_tab}</button>
            <button onClick={() => setActiveTab('cbt')} className={`px-4 py-2 rounded-xl text-sm font-black transition-all whitespace-nowrap ${activeTab === 'cbt' ? 'bg-teal-500 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>{t.cbt_tab}</button>
            <button onClick={() => setActiveTab('safety')} className={`px-4 py-2 rounded-xl text-sm font-black transition-all whitespace-nowrap ${activeTab === 'safety' ? 'bg-teal-500 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>{t.safety_tab}</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-1">
        {activeTab === 'checkin' && (
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-teal-500"></div>
                <label className="block text-slate-800 font-black mb-4 text-lg flex items-center gap-2">
                    <HeartPulse className="w-6 h-6 text-teal-500" />
                    {t.calm_checkin_label}
                </label>
                <textarea 
                    value={inputFeelings} 
                    onChange={(e) => setInputFeelings(e.target.value)} 
                    placeholder={t.calm_checkin_placeholder} 
                    className="w-full h-40 bg-slate-50 border border-slate-200 rounded-[2rem] p-6 text-slate-700 focus:ring-4 focus:ring-teal-100 outline-none transition-all resize-none text-lg shadow-inner" 
                />
                <div className="mt-8 flex flex-col md:flex-row items-center gap-8">
                   <div className="flex-1 w-full">
                      <div className="flex justify-between text-xs font-black text-slate-400 mb-3 uppercase tracking-wider">
                          <span>{t.calm_low} (١)</span>
                          <span className="text-teal-600">ئاستی دڵەڕاوکێ: {anxietyLevel}</span>
                          <span>{t.calm_high} (١٠)</span>
                      </div>
                      <input type="range" min="1" max="10" value={anxietyLevel} onChange={(e) => setAnxietyLevel(parseInt(e.target.value))} className="w-full h-2.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-teal-500" />
                   </div>
                   <button 
                        onClick={handleCheckIn} 
                        disabled={loadingAnalysis || !inputFeelings.trim()} 
                        className="bg-teal-600 text-white px-10 py-4 rounded-2xl font-black text-lg flex items-center gap-3 hover:bg-teal-700 transition-all shadow-xl shadow-teal-500/20 disabled:opacity-50"
                   >
                        {loadingAnalysis ? <Loader2 className="animate-spin w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
                        {t.calm_analyze_btn}
                   </button>
                </div>
            </div>

            {analysisResult && (
              <div className="animate-slide-up p-8 rounded-[2.5rem] border-2 border-teal-100 bg-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none"><Brain className="w-32 h-32" /></div>
                <h3 className="font-black mb-4 flex items-center gap-3 text-teal-800 text-xl">
                    <Sparkles className="w-6 h-6" />{t.calm_smart_analysis}
                </h3>
                <p className="text-lg text-slate-700 leading-loose">
                    <span className="font-black text-teal-600">{t.calm_suggestion}</span> {analysisResult.suggestion}
                </p>
              </div>
            )}

            <button onClick={() => setIsEmergency(true)} className="w-full py-6 bg-rose-50 text-rose-600 border-2 border-dashed border-rose-200 rounded-[2.5rem] font-black text-xl flex items-center justify-center gap-3 hover:bg-rose-100 transition-all group">
                <AlertTriangle className="w-6 h-6 group-hover:scale-125 transition-transform" />
                {t.calm_panic_mode}
            </button>
          </div>
        )}

        {activeTab === 'ladder' && (
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl">
                <h3 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><List className="w-6 h-6 text-teal-500" /> {t.ladder_tab}</h3>
                <div className="flex gap-3">
                    <input value={fearInput} onChange={(e) => setFearInput(e.target.value)} placeholder="ناوی ترسێک بنووسە (بۆ نموونە: تاقیکردنەوە)..." className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 outline-none focus:ring-4 focus:ring-teal-100 transition-all" />
                    <button onClick={handleLadder} disabled={loadingLadder || !fearInput.trim()} className="bg-teal-600 text-white p-4 rounded-2xl shadow-lg hover:bg-teal-700 disabled:opacity-50">
                        {loadingLadder ? <Loader2 className="animate-spin" /> : <Plus className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {ladderResult && (
                <div className="animate-slide-up space-y-4">
                    <h4 className="font-black text-slate-500 uppercase text-[10px] tracking-widest px-4">پلیکانەی ترس بۆ: {ladderResult.fear}</h4>
                    {ladderResult.steps.map((step: any, i: number) => (
                        <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-6 relative group hover:border-teal-200 transition-colors">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl shadow-inner shrink-0 ${step.anxietyLevel > 70 ? 'bg-rose-50 text-rose-500' : step.anxietyLevel > 40 ? 'bg-amber-50 text-amber-500' : 'bg-emerald-50 text-emerald-500'}`}>
                                {step.anxietyLevel}
                            </div>
                            <div className="flex-1">
                                <h5 className="font-black text-slate-800">{step.label}</h5>
                                <p className="text-xs text-slate-400 mt-1">{step.description}</p>
                            </div>
                            <CheckCircle className="w-6 h-6 text-slate-100 group-hover:text-emerald-500 transition-colors" />
                        </div>
                    ))}
                </div>
            )}
          </div>
        )}

        {activeTab === 'cbt' && (
          <div className="space-y-6">
            <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl">
                <h3 className="text-xl font-black text-slate-800 mb-2 flex items-center gap-3"><Brain className="w-6 h-6 text-indigo-500" /> {t.cbt_tab}</h3>
                <p className="text-sm text-slate-500 mb-6">بیرکردنەوەیەکی نەرێنی بنووسە تا پێکەوە بیگۆڕین بۆ بیرکردنەوەیەکی هاوسەنگ.</p>
                <div className="space-y-4">
                    <textarea value={cbtInput} onChange={(e) => setCbtInput(e.target.value)} placeholder="بۆ نموونە: من هیچ کات لەم ئیشەدا سەرکەوتوو نابم..." className="w-full h-32 bg-slate-50 border border-slate-200 rounded-2xl p-6 outline-none focus:ring-4 focus:ring-indigo-100 transition-all resize-none shadow-inner" />
                    <button onClick={handleCBT} disabled={loadingCbt || !cbtInput.trim()} className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-500/20 disabled:opacity-50">
                        {loadingCbt ? <Loader2 className="animate-spin w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                        شیکاری و گۆڕینی بیرکردنەوە
                    </button>
                </div>
            </div>

            {cbtResult && (
                <div className="animate-slide-up grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-rose-50 p-6 rounded-3xl border border-rose-100">
                        <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-3 block">بیرکردنەوەی نەرێنی</span>
                        <p className="text-slate-800 font-bold leading-relaxed">{cbtResult.thought}</p>
                        <div className="mt-4 p-3 bg-white/50 rounded-xl text-xs text-rose-800 font-medium">هەڵەی مێشک: {cbtResult.distortion}</div>
                    </div>
                    <div className="bg-emerald-50 p-6 rounded-3xl border border-emerald-100">
                        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 block">بیرکردنەوەی هاوسەنگ</span>
                        <p className="text-slate-800 font-bold leading-relaxed">{cbtResult.reframing}</p>
                        <div className="mt-4 p-3 bg-white/50 rounded-xl text-xs text-emerald-800 font-medium italic">ئەمە ڕاستییەکی زانستیترە.</div>
                    </div>
                </div>
            )}
          </div>
        )}

        {activeTab === 'safety' && (
            <div className="space-y-6">
                <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl text-right">
                    <h3 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-3"><ShieldAlert className="w-6 h-6 text-rose-500" /> {t.safety_tab}</h3>
                    
                    <div className="space-y-6">
                        <div className="space-y-4">
                            <h4 className="font-bold text-slate-700 flex items-center gap-2"><Thermometer className="w-4 h-4 text-orange-500" /> نیشانەکانی سەرەتا</h4>
                            <div className="flex flex-wrap gap-2">
                                {user.anxietyProfile.safetyPlan.warningSigns.map((s, i) => <span key={i} className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-full text-xs font-bold text-slate-500">{s}</span>)}
                                <button className="px-3 py-1 border-2 border-dashed border-slate-200 rounded-full text-xs text-slate-400 hover:border-teal-300 hover:text-teal-500 transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> زیادکردن</button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h4 className="font-bold text-slate-700 flex items-center gap-2"><Wind className="w-4 h-4 text-teal-500" /> ڕێگاکانی ئارامبوونەوە</h4>
                            <div className="space-y-2">
                                {user.anxietyProfile.safetyPlan.copingStrategies.map((s, i) => (
                                    <div key={i} className="bg-teal-50/30 p-4 rounded-2xl border border-teal-100/50 text-sm font-bold text-teal-900 flex items-center gap-3">
                                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div> {s}
                                    </div>
                                ))}
                                {user.anxietyProfile.safetyPlan.copingStrategies.length === 0 && <p className="text-xs text-slate-400 italic">هیچ شتێک تۆمار نەکراوە.</p>}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h4 className="font-bold text-slate-700 flex items-center gap-2"><UserCheck className="w-4 h-4 text-blue-500" /> کەسانی پشتگیر</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {user.anxietyProfile.safetyPlan.socialSupports.map((s, i) => (
                                    <div key={i} className="bg-blue-50/30 p-4 rounded-2xl border border-blue-100/50 flex justify-between items-center">
                                        <span className="font-bold text-sm text-blue-900">{s}</span>
                                        <Phone className="w-4 h-4 text-blue-400" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-indigo-900 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-500 rounded-full blur-[100px] opacity-10 -ml-32 -mt-32"></div>
                    <div className="relative z-10 text-center">
                        <MessageSquare className="w-12 h-12 mx-auto mb-6 text-indigo-300 opacity-50" />
                        <h4 className="text-2xl font-black mb-4">پەیوەندی ڕاستەوخۆ لەگەڵ هاوڕێ</h4>
                        <p className="text-indigo-200 text-sm mb-8 leading-relaxed max-w-sm mx-auto">ئەگەر حەز دەکەیت بە دەنگ لەگەڵ هاوڕێ قسە بکەیت و باری دەروونیت باشتر بکەیت، بچۆ بۆ بەشی چاتی زیندوو.</p>
                        <button className="bg-white text-indigo-900 px-10 py-4 rounded-2xl font-black text-lg shadow-xl shadow-black/20 hover:scale-[1.03] transition-all flex items-center justify-center gap-3 mx-auto">
                            دەستپێکردنی گفتوگۆ <ArrowRight className="w-6 h-6 rotate-180" />
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default CalmHub;
